import React, { useState } from 'react';
import {
  Upload,
  Download,
  CheckCircle,
  AlertTriangle,
  X,
  FileText,
  Send,
  Eye,
  RefreshCw,
  AlertCircle,
} from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import { toast } from 'sonner';

interface VendorBulkImportProps {
  companyId: string;
  companyName: string;
  user: {
    id: string;
    name: string;
    role: string;
  };
  onComplete?: (results: ImportResults) => void;
  onCancel?: () => void;
}

interface VendorImportRow {
  rowNumber: number;
  contactName: string;
  phoneNumber: string;
  email?: string;
  companyName?: string;
  services?: string;
  notes?: string;
  status: 'pending' | 'valid' | 'invalid' | 'duplicate' | 'sent' | 'failed';
  errors: string[];
  warnings: string[];
}

interface ImportResults {
  total: number;
  successful: number;
  failed: number;
  duplicates: number;
  details: VendorImportRow[];
}

const CSV_TEMPLATE = `contactName,phoneNumber,email,companyName,services,notes
John Smith,555-0101,john@example.com,Smith Services,"Pump Repair, Maintenance",Experienced vendor
Jane Doe,555-0102,jane@example.com,Doe Equipment,"Installation, Emergency Service",24/7 availability
Bob Johnson,555-0103,bob@example.com,Johnson LLC,"Inspection, Maintenance",Licensed and insured`;

export default function VendorBulkImport({
  companyId,
  companyName,
  user,
  onComplete,
  onCancel,
}: VendorBulkImportProps) {
  const [step, setStep] = useState<'upload' | 'preview' | 'sending' | 'complete'>('upload');
  const [file, setFile] = useState<File | null>(null);
  const [importData, setImportData] = useState<VendorImportRow[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [sendProgress, setSendProgress] = useState(0);
  const [results, setResults] = useState<ImportResults | null>(null);

  const downloadTemplate = () => {
    const blob = new Blob([CSV_TEMPLATE], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'vendor_invitation_template.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success('Template downloaded successfully');
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;

    // Validate file type
    if (!selectedFile.name.endsWith('.csv')) {
      toast.error('Please upload a CSV file');
      return;
    }

    // Validate file size (max 5MB)
    if (selectedFile.size > 5 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 5MB');
      return;
    }

    setFile(selectedFile);
    await processFile(selectedFile);
  };

  const processFile = async (file: File) => {
    setIsProcessing(true);

    try {
      const text = await file.text();
      const lines = text.split('\n').filter(line => line.trim());
      
      if (lines.length < 2) {
        toast.error('CSV file is empty or has no data rows');
        setIsProcessing(false);
        return;
      }

      // Parse CSV header
      const header = lines[0].split(',').map(h => h.trim().replace(/"/g, ''));
      
      // Expected columns
      const requiredColumns = ['contactName', 'phoneNumber'];
      const missingColumns = requiredColumns.filter(col => !header.includes(col));
      
      if (missingColumns.length > 0) {
        toast.error(`Missing required columns: ${missingColumns.join(', ')}`);
        setIsProcessing(false);
        return;
      }

      // Parse data rows
      const rows: VendorImportRow[] = [];
      const phoneNumbers = new Set<string>();
      const emails = new Set<string>();

      for (let i = 1; i < lines.length; i++) {
        const line = lines[i].trim();
        if (!line) continue;

        const values = parseCSVLine(line);
        const row: VendorImportRow = {
          rowNumber: i,
          contactName: '',
          phoneNumber: '',
          email: undefined,
          companyName: undefined,
          services: undefined,
          notes: undefined,
          status: 'pending',
          errors: [],
          warnings: [],
        };

        // Map values to fields
        header.forEach((col, index) => {
          const value = values[index]?.trim();
          if (!value) return;

          switch (col) {
            case 'contactName':
              row.contactName = value;
              break;
            case 'phoneNumber':
              row.phoneNumber = value;
              break;
            case 'email':
              row.email = value;
              break;
            case 'companyName':
              row.companyName = value;
              break;
            case 'services':
              row.services = value;
              break;
            case 'notes':
              row.notes = value;
              break;
          }
        });

        // Validation
        if (!row.contactName) {
          row.errors.push('Contact name is required');
        }

        if (!row.phoneNumber) {
          row.errors.push('Phone number is required');
        } else {
          // Validate phone format
          const cleaned = row.phoneNumber.replace(/\D/g, '');
          if (cleaned.length < 10) {
            row.errors.push('Invalid phone number (must be at least 10 digits)');
          } else {
            // Format to E.164
            row.phoneNumber = `+1${cleaned.slice(-10)}`;
            
            // Check for duplicates within file
            if (phoneNumbers.has(row.phoneNumber)) {
              row.status = 'duplicate';
              row.warnings.push('Duplicate phone number in this file');
            } else {
              phoneNumbers.add(row.phoneNumber);
            }
          }
        }

        // Email validation
        if (row.email) {
          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          if (!emailRegex.test(row.email)) {
            row.errors.push('Invalid email format');
          } else if (emails.has(row.email)) {
            row.warnings.push('Duplicate email in this file');
          } else {
            emails.add(row.email);
          }
        }

        // Set status
        if (row.errors.length === 0) {
          row.status = row.status === 'duplicate' ? 'duplicate' : 'valid';
        } else {
          row.status = 'invalid';
        }

        rows.push(row);
      }

      setImportData(rows);
      setStep('preview');
      toast.success(`Loaded ${rows.length} rows from CSV`);
      
    } catch (error) {
      console.error('Error processing CSV:', error);
      toast.error('Failed to process CSV file. Please check the format.');
    } finally {
      setIsProcessing(false);
    }
  };

  const parseCSVLine = (line: string): string[] => {
    const result: string[] = [];
    let current = '';
    let inQuotes = false;

    for (let i = 0; i < line.length; i++) {
      const char = line[i];

      if (char === '"') {
        inQuotes = !inQuotes;
      } else if (char === ',' && !inQuotes) {
        result.push(current.trim().replace(/^"|"$/g, ''));
        current = '';
      } else {
        current += char;
      }
    }

    result.push(current.trim().replace(/^"|"$/g, ''));
    return result;
  };

  const handleSendInvitations = async () => {
    const validRows = importData.filter(row => row.status === 'valid');
    
    if (validRows.length === 0) {
      toast.error('No valid rows to send invitations');
      return;
    }

    setStep('sending');
    setSendProgress(0);

    try {
      const updatedRows = [...importData];
      let successCount = 0;
      let failCount = 0;

      // Send invitations one by one
      for (let i = 0; i < validRows.length; i++) {
        const row = validRows[i];
        
        try {
          // In production, call sendVendorInvitationSMS Cloud Function
          await new Promise(resolve => setTimeout(resolve, 500)); // Simulate API call
          
          // Update row status
          const rowIndex = updatedRows.findIndex(r => r.rowNumber === row.rowNumber);
          updatedRows[rowIndex].status = 'sent';
          successCount++;
          
        } catch (error) {
          const rowIndex = updatedRows.findIndex(r => r.rowNumber === row.rowNumber);
          updatedRows[rowIndex].status = 'failed';
          updatedRows[rowIndex].errors.push('Failed to send invitation');
          failCount++;
        }

        // Update progress
        setSendProgress(((i + 1) / validRows.length) * 100);
        setImportData([...updatedRows]);
      }

      // Generate results
      const finalResults: ImportResults = {
        total: importData.length,
        successful: successCount,
        failed: failCount + importData.filter(r => r.status === 'invalid').length,
        duplicates: importData.filter(r => r.status === 'duplicate').length,
        details: updatedRows,
      };

      setResults(finalResults);
      setStep('complete');
      
      if (onComplete) {
        onComplete(finalResults);
      }

      toast.success(`Successfully sent ${successCount} invitations`);
      
    } catch (error) {
      console.error('Error sending invitations:', error);
      toast.error('Failed to send invitations');
      setStep('preview');
    }
  };

  const getStatusBadge = (status: VendorImportRow['status']) => {
    const badges = {
      pending: <Badge variant="secondary">Pending</Badge>,
      valid: <Badge className="bg-green-100 text-green-800">Valid</Badge>,
      invalid: <Badge className="bg-red-100 text-red-800">Invalid</Badge>,
      duplicate: <Badge className="bg-yellow-100 text-yellow-800">Duplicate</Badge>,
      sent: <Badge className="bg-blue-100 text-blue-800">Sent</Badge>,
      failed: <Badge className="bg-red-100 text-red-800">Failed</Badge>,
    };
    return badges[status];
  };

  const getStatusIcon = (status: VendorImportRow['status']) => {
    const icons = {
      pending: <RefreshCw className="w-4 h-4 text-gray-400" />,
      valid: <CheckCircle className="w-4 h-4 text-green-600" />,
      invalid: <X className="w-4 h-4 text-red-600" />,
      duplicate: <AlertTriangle className="w-4 h-4 text-yellow-600" />,
      sent: <CheckCircle className="w-4 h-4 text-blue-600" />,
      failed: <AlertCircle className="w-4 h-4 text-red-600" />,
    };
    return icons[status];
  };

  const stats = {
    total: importData.length,
    valid: importData.filter(r => r.status === 'valid').length,
    invalid: importData.filter(r => r.status === 'invalid').length,
    duplicate: importData.filter(r => r.status === 'duplicate').length,
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="border-b pb-4">
        <h2 className="text-2xl font-bold text-gray-900">Bulk Vendor Invitations</h2>
        <p className="text-gray-600 mt-1">
          Upload a CSV file to send SMS invitations to multiple vendors at once
        </p>
      </div>

      {/* Step Indicator */}
      <div className="flex items-center justify-center gap-4">
        {[
          { key: 'upload', label: 'Upload CSV', icon: Upload },
          { key: 'preview', label: 'Preview & Validate', icon: Eye },
          { key: 'sending', label: 'Sending', icon: Send },
          { key: 'complete', label: 'Complete', icon: CheckCircle },
        ].map((s, index) => {
          const Icon = s.icon;
          const isActive = step === s.key;
          const isCompleted = 
            (step === 'preview' && ['upload'].includes(s.key)) ||
            (step === 'sending' && ['upload', 'preview'].includes(s.key)) ||
            (step === 'complete' && ['upload', 'preview', 'sending'].includes(s.key));
          
          return (
            <React.Fragment key={s.key}>
              <div className="flex flex-col items-center">
                <div className={`
                  w-12 h-12 rounded-full flex items-center justify-center mb-2 transition-all
                  ${isActive ? 'bg-blue-600 text-white ring-4 ring-blue-100' :
                    isCompleted ? 'bg-green-600 text-white' :
                    'bg-gray-200 text-gray-500'}
                `}>
                  <Icon className="w-6 h-6" />
                </div>
                <span className={`text-xs font-medium ${isActive ? 'text-blue-600' : 'text-gray-500'}`}>
                  {s.label}
                </span>
              </div>
              {index < 3 && (
                <div className={`w-16 h-1 mt-6 ${isCompleted ? 'bg-green-600' : 'bg-gray-200'}`} />
              )}
            </React.Fragment>
          );
        })}
      </div>

      {/* Upload Step */}
      {step === 'upload' && (
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Step 1: Download Template or Upload CSV
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Download Template */}
              <div>
                <h3 className="font-semibold text-gray-900 mb-3">Option 1: Start with Template</h3>
                <p className="text-sm text-gray-600 mb-4">
                  Download our CSV template with the correct format and fill in your vendor information.
                </p>
                <Button onClick={downloadTemplate} variant="outline">
                  <Download className="w-4 h-4 mr-2" />
                  Download CSV Template
                </Button>
              </div>

              <div className="border-t pt-6">
                <h3 className="font-semibold text-gray-900 mb-3">Option 2: Upload Your CSV</h3>
                <p className="text-sm text-gray-600 mb-4">
                  Upload a CSV file with vendor information. Required columns: contactName, phoneNumber
                </p>
                
                <label className="flex items-center justify-center w-full h-48 px-4 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-blue-500 transition-colors bg-gray-50">
                  <div className="text-center">
                    {file ? (
                      <>
                        <FileText className="w-12 h-12 text-blue-600 mx-auto mb-3" />
                        <p className="text-sm font-medium text-gray-900">{file.name}</p>
                        <p className="text-xs text-gray-500 mt-1">
                          {(file.size / 1024).toFixed(1)} KB
                        </p>
                        {isProcessing && (
                          <p className="text-xs text-blue-600 mt-2 flex items-center justify-center">
                            <RefreshCw className="w-3 h-3 mr-1 animate-spin" />
                            Processing...
                          </p>
                        )}
                      </>
                    ) : (
                      <>
                        <Upload className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                        <p className="text-sm text-gray-600">Click to upload or drag and drop</p>
                        <p className="text-xs text-gray-500 mt-1">CSV files only, up to 5MB</p>
                      </>
                    )}
                  </div>
                  <input
                    type="file"
                    accept=".csv"
                    onChange={handleFileSelect}
                    className="hidden"
                    disabled={isProcessing}
                  />
                </label>
              </div>

              {/* CSV Format Guide */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="font-medium text-blue-900 mb-2">CSV Format Requirements:</h4>
                <ul className="text-sm text-blue-800 space-y-1">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <span><strong>contactName</strong> - Full name of the contact person (required)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <span><strong>phoneNumber</strong> - 10-digit phone number (required)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <span><strong>email</strong> - Email address (optional)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <span><strong>companyName</strong> - Vendor company name (optional)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <span><strong>services</strong> - Services offered, comma-separated (optional)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <span><strong>notes</strong> - Additional notes (optional)</span>
                  </li>
                </ul>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Preview Step */}
      {step === 'preview' && (
        <div className="space-y-6">
          {/* Summary Stats */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Total Rows</p>
                    <p className="text-2xl font-bold text-gray-900 mt-1">{stats.total}</p>
                  </div>
                  <FileText className="w-8 h-8 text-gray-400" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Valid</p>
                    <p className="text-2xl font-bold text-green-600 mt-1">{stats.valid}</p>
                  </div>
                  <CheckCircle className="w-8 h-8 text-green-600" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Invalid</p>
                    <p className="text-2xl font-bold text-red-600 mt-1">{stats.invalid}</p>
                  </div>
                  <X className="w-8 h-8 text-red-600" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Duplicates</p>
                    <p className="text-2xl font-bold text-yellow-600 mt-1">{stats.duplicate}</p>
                  </div>
                  <AlertTriangle className="w-8 h-8 text-yellow-600" />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Validation Messages */}
          {(stats.invalid > 0 || stats.duplicate > 0) && (
            <Card className="border-yellow-200 bg-yellow-50">
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-yellow-600 mt-0.5" />
                  <div>
                    <p className="font-medium text-yellow-900">Validation Issues Detected</p>
                    <p className="text-sm text-yellow-800 mt-1">
                      {stats.invalid > 0 && `${stats.invalid} row(s) have validation errors and will be skipped. `}
                      {stats.duplicate > 0 && `${stats.duplicate} duplicate phone number(s) detected. `}
                      Only valid rows will receive invitations.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Preview Table */}
          <Card>
            <CardHeader>
              <CardTitle>Preview & Validate</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Row</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Contact Name</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Phone</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Company</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Issues</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {importData.map((row) => (
                      <tr key={row.rowNumber} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900">{row.rowNumber}</td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            {getStatusIcon(row.status)}
                            {getStatusBadge(row.status)}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900">{row.contactName || '-'}</td>
                        <td className="px-4 py-3 text-sm text-gray-900">{row.phoneNumber || '-'}</td>
                        <td className="px-4 py-3 text-sm text-gray-900">{row.email || '-'}</td>
                        <td className="px-4 py-3 text-sm text-gray-900">{row.companyName || '-'}</td>
                        <td className="px-4 py-3">
                          {row.errors.length > 0 && (
                            <div className="space-y-1">
                              {row.errors.map((error, idx) => (
                                <p key={idx} className="text-xs text-red-600 flex items-start gap-1">
                                  <X className="w-3 h-3 mt-0.5 flex-shrink-0" />
                                  {error}
                                </p>
                              ))}
                            </div>
                          )}
                          {row.warnings.length > 0 && (
                            <div className="space-y-1">
                              {row.warnings.map((warning, idx) => (
                                <p key={idx} className="text-xs text-yellow-600 flex items-start gap-1">
                                  <AlertTriangle className="w-3 h-3 mt-0.5 flex-shrink-0" />
                                  {warning}
                                </p>
                              ))}
                            </div>
                          )}
                          {row.errors.length === 0 && row.warnings.length === 0 && (
                            <span className="text-xs text-green-600">✓ Ready</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          {/* Actions */}
          <div className="flex items-center justify-between pt-4 border-t">
            <Button variant="outline" onClick={() => setStep('upload')}>
              ← Back to Upload
            </Button>
            <Button
              onClick={handleSendInvitations}
              className="bg-blue-600 hover:bg-blue-700"
              disabled={stats.valid === 0}
            >
              <Send className="w-4 h-4 mr-2" />
              Send {stats.valid} Invitation{stats.valid !== 1 ? 's' : ''}
            </Button>
          </div>
        </div>
      )}

      {/* Sending Step */}
      {step === 'sending' && (
        <Card>
          <CardContent className="py-12">
            <div className="max-w-md mx-auto text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Send className="w-8 h-8 text-blue-600 animate-pulse" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Sending Invitations...</h3>
              <p className="text-gray-600 mb-6">
                Please wait while we send SMS invitations to {stats.valid} vendors
              </p>
              <Progress value={sendProgress} className="mb-4" />
              <p className="text-sm text-gray-500">
                {Math.round(sendProgress)}% complete
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Complete Step */}
      {step === 'complete' && results && (
        <div className="space-y-6">
          <Card className="border-green-200 bg-green-50">
            <CardContent className="p-8 text-center">
              <div className="w-16 h-16 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-green-900 mb-2">Import Complete!</h3>
              <p className="text-green-800 mb-6">
                Successfully sent {results.successful} out of {results.total} invitations
              </p>
              
              <div className="grid grid-cols-3 gap-4 max-w-md mx-auto">
                <div className="bg-white rounded-lg p-4">
                  <p className="text-3xl font-bold text-green-600">{results.successful}</p>
                  <p className="text-sm text-gray-600 mt-1">Sent</p>
                </div>
                <div className="bg-white rounded-lg p-4">
                  <p className="text-3xl font-bold text-red-600">{results.failed}</p>
                  <p className="text-sm text-gray-600 mt-1">Failed</p>
                </div>
                <div className="bg-white rounded-lg p-4">
                  <p className="text-3xl font-bold text-yellow-600">{results.duplicates}</p>
                  <p className="text-sm text-gray-600 mt-1">Duplicates</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Detailed Results */}
          <Card>
            <CardHeader>
              <CardTitle>Detailed Results</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {results.details.map((row) => (
                  <div key={row.rowNumber} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      {getStatusIcon(row.status)}
                      <div>
                        <p className="font-medium text-gray-900">{row.contactName}</p>
                        <p className="text-sm text-gray-500">{row.phoneNumber}</p>
                      </div>
                    </div>
                    {getStatusBadge(row.status)}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Actions */}
          <div className="flex items-center justify-center gap-4 pt-4">
            <Button variant="outline" onClick={() => {
              setStep('upload');
              setFile(null);
              setImportData([]);
              setResults(null);
            }}>
              Import More Vendors
            </Button>
            <Button onClick={onCancel} className="bg-blue-600 hover:bg-blue-700">
              Done
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
